import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAnalytics } from '../utils/analytics';
import {
  NOT_INTERESTED_OPTIONS,
  buildNotInterestedSignal,
  forgetDismissal,
  rememberDismissal,
  type NotInterestedSource,
  type NotInterestedStrength,
  type NotInterestedTarget,
} from '../utils/not-interested';

interface NotInterestedButtonProps {
  /** What the showcase is about, and therefore what dismissing it means. */
  target: NotInterestedTarget;
  source: NotInterestedSource;
  /**
   * Told after the signal is sent, so the surrounding row can collapse itself.
   * `undone` distinguishes taking it back from a fresh dismissal.
   */
  onChange?: (state: { dismissed: boolean; strength?: NotInterestedStrength }) => void;
  className?: string;
}

/**
 * The control that lets a reader push a whole showcase down.
 *
 * IT SAYS WHAT IT DOES
 * --------------------
 * This was an X. An X on a row of listings reads as "close this", which is a
 * layout action, and the readers who used it were not knowingly training their
 * feed -- the strongest negative signal on the site was being collected by
 * accident. The words are the fix; there is no icon, because adding one back
 * next to the label just reintroduces the thing people were reading.
 *
 * GREY UNTIL IT IS POINTED AT
 * ---------------------------
 * A showcase heading is not the place for a control that competes with the
 * listings underneath it, so at rest this is grey text and a TRANSPARENT border.
 * The border is there the whole time, not added on hover -- a border that appears
 * from nothing shifts everything beside it by two pixels on every mouse-over.
 * Hover only colours it in, which is what turns the words into a badge.
 */
const NotInterestedButton: React.FC<NotInterestedButtonProps> = ({
  target,
  source,
  onChange,
  className = '',
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [strength, setStrength] = useState<NotInterestedStrength | null>(null);
  const dismissed = strength !== null;
  const rootRef = useRef<HTMLDivElement | null>(null);

  const send = useCallback(
    (params: { strength?: NotInterestedStrength; undo?: boolean }) => {
      const analytics = getAnalytics();
      // Guarded because the tracker is absent when storage is blocked. The
      // remembered dismissal below still applies, so the reader's click does
      // something visible even when the signal cannot be recorded.
      if (analytics) {
        analytics.track(
          buildNotInterestedSignal({ target, source, ...params }) as unknown as Parameters<
            typeof analytics.track
          >[0],
        );
      }
    },
    [target, source],
  );

  const dismiss = useCallback(
    (chosen: NotInterestedStrength) => {
      send({ strength: chosen });
      rememberDismissal(target, chosen);
      setMenuOpen(false);
      setStrength(chosen);
      onChange?.({ dismissed: true, strength: chosen });
    },
    [send, target, onChange],
  );

  const undo = useCallback(() => {
    send({ undo: true });
    forgetDismissal(target);
    setStrength(null);
    onChange?.({ dismissed: false });
  }, [send, target, onChange]);

  // Close the reason menu on an outside click or on Escape. Without this the
  // menu survives a click on the row behind it, and a reader who has changed
  // their mind has no way out of it but to pick one of the answers.
  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  if (dismissed) {
    return (
      <div
        className={`inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 ${className}`}
        data-testid="not-interested-done"
      >
        <span>
          {strength === 'show_none' ? 'You won\u2019t see this again' : 'Showing you less like this'}
        </span>
        <button
          type="button"
          onClick={undo}
          className="rounded-full border border-gray-400 px-2 py-0.5 font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-700"
          data-testid="not-interested-undo"
        >
          Undo
        </button>
      </div>
    );
  }

  return (
    <div className={`relative inline-block ${className}`} ref={rootRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={`Not interested in ${target.label}`}
        className={
          'inline-flex items-center whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium ' +
          'transition-colors ' +
          // At rest: grey on a border that is present but invisible.
          'border-transparent text-gray-500 ' +
          // Pointed at (or open): the badge fills in.
          'hover:border-gray-400 hover:text-gray-700 ' +
          'focus-visible:border-gray-400 focus-visible:text-gray-700 focus-visible:outline-none ' +
          (menuOpen ? 'border-gray-400 text-gray-700 dark:border-gray-500 dark:text-gray-200 ' : '') +
          'dark:text-gray-400 dark:hover:border-gray-500 dark:hover:text-gray-200'
        }
        data-testid="not-interested-button"
      >
        Not Interested
      </button>

      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-56 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
          data-testid="not-interested-menu"
        >
          <p className="border-b border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-200">
            {target.label}
          </p>
          {/* Two answers, and the hints are load-bearing: they are the only
              thing telling a reader that one of these is reversible-ish and the
              other is them saying stop. */}
          <div className="py-1">
            {NOT_INTERESTED_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="menuitem"
                onClick={() => dismiss(option.value)}
                className="block w-full px-3 py-2 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                data-testid={`not-interested-${option.value}`}
              >
                <span className="block text-sm text-gray-700 dark:text-gray-200">
                  {option.label}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  {option.hint}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotInterestedButton;
